import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Clapperboard, Home, MessageSquareText } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import type { ContentPackItemKind } from '../../../common/hooks/use-content-packs-api.hook';

export const KINDS: ContentPackItemKind[] = ['room', 'packshot', 'clip', 'post'];

const ICON: Record<ContentPackItemKind, React.ReactNode> = {
    room: <Home />,
    packshot: <Box />,
    clip: <Clapperboard />,
    post: <MessageSquareText />,
};

/**
 * The kinds a pack will make, with what each depends on added — the same rule
 * as the API (content-pack-plan.ts): a clip is filmed from the room shot, a
 * post needs a picture. Nothing at all means the full pack.
 */
export function normaliseKinds(kinds: ContentPackItemKind[]): ContentPackItemKind[] {
    const set = new Set(kinds.filter((k) => KINDS.includes(k)));
    if (!set.size) return [...KINDS];
    if (set.has('clip')) set.add('room');
    if (set.has('post') && !set.has('room') && !set.has('packshot')) set.add('room');
    return KINDS.filter((k) => set.has(k));
}

/** What a pack contains: one row per kind, its price, and how many posts. */
export const KindPicker: React.FC<{
    kinds: ContentPackItemKind[];
    onKinds: (kinds: ContentPackItemKind[]) => void;
    posts: number;
    onPosts: (n: number) => void;
    prices?: Partial<Record<ContentPackItemKind, number>>;
    disabled?: boolean;
}> = ({ kinds, onKinds, posts, onPosts, prices, disabled }) => {
    const { t } = useTranslation();
    const effective = normaliseKinds(kinds);
    const toggle = (k: ContentPackItemKind, on: boolean) => {
        const next = on ? [...kinds, k] : kinds.filter((x) => x !== k);
        // Turning off what another kind needs turns that one off too.
        const cleaned = !on && k === 'room' ? next.filter((x) => x !== 'clip') : next;
        if (!cleaned.length) return;
        onKinds(normaliseKinds(cleaned));
    };
    return (
        <div className="flex flex-col divide-y rounded-lg border" data-testid="kind-picker">
            {KINDS.map((k) => {
                const on = effective.includes(k);
                const price = prices?.[k];
                return (
                    <div key={k} className={cn('flex items-center gap-3 px-3 py-2.5', !on && 'text-muted-foreground')}>
                        <Checkbox id={`kind-${k}`} checked={on} disabled={disabled} onCheckedChange={(v) => toggle(k, v === true)} />
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-md border bg-muted/50 text-muted-foreground [&_svg]:size-3.5">{ICON[k]}</span>
                        <label htmlFor={`kind-${k}`} className="min-w-0 flex-1 cursor-pointer">
                            <span className="block text-sm text-foreground">{t(`CONTENT_PACKS.KIND.${k.toUpperCase()}`)}</span>
                            <span className="block text-xs text-muted-foreground">{t(`CONTENT_PACKS.KIND_HINT.${k.toUpperCase()}`)}</span>
                        </label>
                        {k === 'post' && on && (
                            <NativeSelect
                                aria-label={t('CONTENT_PACKS.DIALOG.POSTS_COUNT')}
                                value={String(posts)}
                                disabled={disabled}
                                onChange={(e) => onPosts(Number(e.target.value))}
                                className="w-16"
                            >
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <NativeSelectOption key={n} value={String(n)}>
                                        {n}
                                    </NativeSelectOption>
                                ))}
                            </NativeSelect>
                        )}
                        <span className="w-20 shrink-0 text-end text-xs tabular-nums text-muted-foreground">
                            {price === undefined ? '' : price === 0 ? t('CONTENT_PACKS.DIALOG.FREE') : t('CONTENT_PACKS.PACK.CREDITS', { n: price })}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};
